import { MediaMatcher } from '@angular/cdk/layout';
import { OverlayContainer } from '@angular/cdk/overlay';
import { NestedTreeControl } from '@angular/cdk/tree';
import { DOCUMENT } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, HostBinding, Inject, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { MatTreeNestedDataSource } from '@angular/material/tree';
import { NavigationEnd, RouteConfigLoadEnd, RouteConfigLoadStart, Router } from '@angular/router';
import { IRouteNode, LoaderService, SecurityGroups, TokenService } from '@morningstar/shared/auth';
import { PayNowService } from '@morningstar/shared/base/pay-now-service';
import { StorageService, StorageTypes } from '@morningstar/shared/base/storage-service';
import { environment } from '@morningstar/shared/environment';
import { Client, ClientService } from '@morningstar/shared/services/client';
import { InvoiceService } from '@morningstar/shared/services/cutoff';
import { ILocation, LocationsService } from '@morningstar/shared/services/locations';
import { BehaviorSubject, combineLatest, merge, Observable, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { RouteValidationService } from './authorization/route-validation.service';
import { DefaultLocationDialogComponent } from './shared/dialogs/default-location/default-location-dialog.component';
import { MenuIcons } from './shared/menu-icons';
import { Themes } from './shared/themes';

@Component({
  selector: 'wsg-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit, OnDestroy, AfterViewInit {
  bceLink = environment.bceLink;

  locations: ILocation[];
  clients: Client[];

  currentClient: string;

  currentRoute$ = new BehaviorSubject<string>('');
  isPayNowLink$ = combineLatest([this.currentRoute$, this.payNowService.msClient$]).pipe(
    map(([route, msClient]) => route.indexOf('geek-pay') !== -1 && (msClient?.toUpperCase() === 'QUESTW' || msClient?.toUpperCase() === 'OBC05'))
  );
  
  defaultLocationDialogComponent = DefaultLocationDialogComponent;
  // handling responsive sidenav
  mobileQuery: MediaQueryList;
  private _mobileQueryListener: () => void;

  userSecurityGroup = 1000;
  userIsAuthenticated = false;
  securityGroups = SecurityGroups;
  loadingRoute = false;
  refreshingToken = false;
  notBceHome = true;
  splashPage = false;

  themes = Themes;

  theme$ = this.storageService.theme$;
  clientListIsActive$ = this.storageService.clientListIsActive$;
  futureClients$ = this.storageService.futureClients$;

  onThemeChange$ = this.theme$.pipe(
    tap(theme => this.applyTheme(theme))
  );

  subscription = merge(
    this.onThemeChange$
  ).subscribe();

  clientName$ = this.storageService.clientName$;
  locationName$ = this.storageService.locationName$;

  nestedTreeControl: NestedTreeControl<IRouteNode>;
  nestedDataSource = new MatTreeNestedDataSource<IRouteNode>();

  constructor(private tokenService: TokenService, public router: Router, changeDetectorRef: ChangeDetectorRef, media: MediaMatcher,
              private storageService: StorageService, private routeValidationService: RouteValidationService,
              private elementRef: ElementRef, private renderer: Renderer2, private loaderService: LoaderService,
              private locationService: LocationsService, private clientService: ClientService, private invoiceService: InvoiceService,
              private payNowService: PayNowService,
              public overlayContainer: OverlayContainer, protected httpClient: HttpClient, @Inject(DOCUMENT) private document: Document){

    tokenService.userStatusChanged$.subscribe(user => {
      this.userIsAuthenticated = user.isAuthenticated;
      this.userSecurityGroup = user.securityGroup;

      this.nestedDataSource.data = this.getVisibleRoutes() || new Array<IRouteNode>();

      this.setListItemPadding();

      if (this.userIsAuthenticated) {
        this.storageService.clear();

        this.locationService.get(false).subscribe(locations => {
          this.locations = locations;
          let defaultLocation = this.storageService.getItem(StorageTypes.location);

          if (!defaultLocation) {
            defaultLocation = this.tokenService.getDefaultLocation();
          }

          this.storageService.setItem(StorageTypes.location, defaultLocation);

          this.clientService.getClientsByLocation(defaultLocation, true, true).subscribe(clients => {
            this.clients = clients;
            let storageClient = this.storageService.getItem(StorageTypes.client);

            if (!this.clients.find(c => c.id === storageClient) && clients.length > 0) {
              storageClient = clients[0].id;
            }

            this.storageService.setItem(StorageTypes.client, storageClient);
          });
        });

        this.invoiceService.getCutoffDate();
      }
    });

    this.nestedTreeControl = new NestedTreeControl<IRouteNode>(this.getChildren);
    this.nestedDataSource.data = this.tokenService.getRouteNodes() || new Array<IRouteNode>();

    // handling responsive sidenav
    this.mobileQuery = media.matchMedia('(max-width: 600px)');
    this._mobileQueryListener = () => changeDetectorRef.detectChanges();
    this.mobileQuery.addListener(this._mobileQueryListener);
  }

  @HostBinding('class') componentCssClass;
  applyTheme(theme) {
    this.overlayContainer.getContainerElement().classList.remove(Themes.light, Themes.dark)
    this.overlayContainer.getContainerElement().classList.add(theme);
    this.componentCssClass = theme;

    let themeEndpoint = '';
    if (theme === Themes.dark) {
      themeEndpoint = 'https://cdn.syncfusion.com/ej2/material-dark.css';
    } else if (theme === Themes.light) {
      themeEndpoint = 'https://cdn.syncfusion.com/ej2/material.css';
    }

    this.document.getElementById('scheduler-theme').setAttribute('href', themeEndpoint);
  }

  switchTheme() {
    let currentTheme = this.storageService.getItem(StorageTypes.theme);
    if (currentTheme === Themes.dark) {
      this.storageService.setItem(StorageTypes.theme, Themes.light);
    } else if (!currentTheme || currentTheme === Themes.light) {
      this.storageService.setItem(StorageTypes.theme, Themes.dark);
    }
  }

  async ngOnInit() {
    if (!this.userIsAuthenticated && this.tokenService.isRefreshTokenValid()) {
      await this.tokenService.refreshAuthToken();
    }

    this.userIsAuthenticated = this.tokenService.isAuthTokenValid();
    this.userSecurityGroup = this.tokenService.getSecurityGroup();

    this.storageService.returnLinkSubject.subscribe(link => {
      this.bceLink = link;
    });

    const componentContext = this;

    this.router.events.subscribe(event => {
      if (event instanceof RouteConfigLoadStart) {
          componentContext.loadingRoute = true;
      } else if (event instanceof RouteConfigLoadEnd) {
          componentContext.loadingRoute = false;
      } 
      
      if (event instanceof NavigationEnd) {
        this.currentRoute$.next(event.url);
      }
    });

    this.loaderService.getRefreshingTokenStatus().subscribe(isRefreshing => {
      if (isRefreshing) {
        this.refreshingToken = true;
      } else {
        this.refreshingToken = false;
      }
    });
  }

  public ngAfterViewInit(): void {
    this.setListItemPadding();
  }

  scrollToContactUsForm() {
    const elements = this.elementRef.nativeElement.querySelectorAll('#divContactUsForm') as HTMLElement[];
    elements[0].scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
  }

  setListItemPadding() {
    const listItems = this.elementRef.nativeElement.querySelectorAll('.mat-list-item-content') as HTMLElement[];
    listItems.forEach(listItem => {
      this.renderer.setStyle(listItem, 'padding', '0px');
    });
  }

  validateSecurityGroup(targetSecurityGroup: SecurityGroups): boolean {
    return this.userIsAuthenticated && this.routeValidationService.validateSecurityGroup(targetSecurityGroup, this.userSecurityGroup);
  }

  validateRoute(path: string) {
    return this.routeValidationService.validateRoute(path);
  }

  getChildren = (node: IRouteNode): Observable<IRouteNode[]> => of(node.children);
  hasNestedChild = (_: number, nodeData: IRouteNode) => nodeData.children && nodeData.children.length > 0 && nodeData.children.filter(c => !c.hidden).length > 0;

  getVisibleRoutes(): IRouteNode[] {
    const routes = [];

    const routeNodes = this.tokenService.getRouteNodes();

    if (routeNodes == null) {
      return null;
    }

    for (let i = 0; i < routeNodes.length; i++) {
      const routeNode = this.getUpdatedRouteNode(routeNodes[i]);

      if (routeNode != null) {
        routeNode.icon = MenuIcons.routeIcons[routeNode.name];
        routes.push(routeNode);
      }
    }

    return routes;
  }

  getUpdatedRouteNode(routeNode: IRouteNode) {
    routeNode.icon = MenuIcons.routeIcons[routeNode.name];

    if (routeNode.children == null || routeNode.children.length === 0) {
      return routeNode;
    }

    for (let i = routeNode.children.length - 1; i >= 0; i--) {
      if (routeNode.children[i].hidden) {
        routeNode.children.splice(i, 1);
      }
    }

    return routeNode;
  }

  ngOnDestroy(): void {
    // drop listener for responsive sidenav
    this.mobileQuery.removeListener(this._mobileQueryListener);
    this.subscription.unsubscribe();
  }
}
